import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardShell from "../../components/DashboardShell";
import LiveFeed from "../../components/LiveFeed";
import useLiveDecisions from "../../lib/useLiveDecisions";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ShieldCheck, XCircle, Eye, AlertTriangle, Activity, DollarSign, ArrowUpRight,
} from "lucide-react";

const kpiMeta = {
  total:  { key: "total",  label: "REQUESTS SCREENED", icon: Activity,      color: "#FFD98A" },
  deny:   { key: "deny",   label: "DENIED",            icon: XCircle,       color: "#ef4444" },
  redact: { key: "redact", label: "REDACTED",          icon: Eye,           color: "#f59e0b" },
  flag:   { key: "flag",   label: "FLAGGED",           icon: AlertTriangle, color: "#FF6B35" },
};

export default function Overview() {
  const [stats, setStats] = useState(null);
  const { status, decisions: liveDecisions, lastAt, subscribers } = useLiveDecisions();
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    api.get("/v1/decisions/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // When a fresh live decision arrives, bump counters + refresh series.
  const [liveDelta, setLiveDelta] = useState({ total: 0, allow: 0, deny: 0, redact: 0, flag: 0 });
  const [flashKey, setFlashKey] = useState(null);

  useEffect(() => {
    if (liveDecisions.length === 0) return;
    const newest = liveDecisions[0];
    if (!newest?.id || seenIdsRef.current.has(newest.id)) return;
    seenIdsRef.current.add(newest.id);
    setLiveDelta((d) => ({
      ...d,
      total: d.total + 1,
      [newest.action]: (d[newest.action] || 0) + 1,
    }));
    setFlashKey({ k: newest.action, at: Date.now() });
    const t = setTimeout(() => setFlashKey(null), 900);
    return () => clearTimeout(t);
  }, [liveDecisions]);

  const kpis = useMemo(() => {
    const base = {
      total:  (stats?.total ?? 0) + liveDelta.total,
      deny:   (stats?.counts?.deny   ?? 0) + liveDelta.deny,
      redact: (stats?.counts?.redact ?? 0) + liveDelta.redact,
      flag:   (stats?.counts?.flag   ?? 0) + liveDelta.flag,
    };
    return ["total", "deny", "redact", "flag"].map((k) => ({
      key: k,
      value: stats ? base[k] : "—",
    }));
  }, [stats, liveDelta]);

  // Merge server 24h series with live deltas so the chart moves in real-time.
  const chartData = useMemo(() => {
    const src = stats?.series || [];
    if (!src.length || !liveDecisions.length) return src;
    const clone = src.map((r) => ({ ...r }));
    const lastBucket = clone[clone.length - 1];
    for (const d of liveDecisions) {
      if (!seenIdsRef.current.has(d.id)) continue;
      // Attribute to the last (current) bucket.
      lastBucket[d.action] = (lastBucket[d.action] || 0) + (chartDeltaSeen.has(d.id) ? 0 : 1);
      chartDeltaSeen.add(d.id);
    }
    return clone;
  }, [stats, liveDecisions]);

  const perfCards = [
    { label: "Avg latency",         value: stats?.avg_latency_ms ?? "—", unit: " ms",  testid: "perf-avg-latency" },
    { label: "Denial rate",         value: stats?.denial_rate ?? "—",    unit: " %",   testid: "perf-denial-rate" },
    { label: "Provider spend (est.)", value: stats ? `$${stats.total_cost_usd}` : "—", unit: "",  testid: "perf-spend" },
  ];

  return (
    <DashboardShell
      title="Sentry & Observatory"
      subtitle="Enforcement + observability across every AI request."
      actions={
        <Link to="/app/playground" className="btn-primary text-sm" data-testid="observatory-run-request">
          Run a request
        </Link>
      }
    >
      <div className="grid md:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const M = kpiMeta[k.key];
          const flashing = flashKey && (flashKey.k === k.key || k.key === "total");
          return (
            <div
              key={k.key}
              data-testid={`kpi-${k.key}`}
              className={`rounded-xl bg-white/5 border border-white/10 p-5 ${flashing ? "kpi-flash" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className="eyebrow" style={{ color: M.color, fontSize: 10 }}>{M.label}</div>
                <M.icon size={14} className="opacity-60" />
              </div>
              <div className="font-display text-3xl font-bold mt-2">{k.value}</div>
              <div className="text-[11px] text-cream/50 mt-1">last 30 days</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-xl bg-white/5 border border-white/10 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow text-[#FFD98A]" style={{ fontSize: 10 }}>DECISIONS · LAST 24H</div>
              <div className="font-display text-lg mt-1">Gateway throughput by action</div>
            </div>
            <div className="chip chip-cream text-[10px]">P95 &lt; 4ms</div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(247,244,239,0.08)" />
                <XAxis dataKey="hour" stroke="rgba(247,244,239,0.5)" style={{ fontSize: 11 }} />
                <YAxis stroke="rgba(247,244,239,0.5)" style={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A0E1A", border: "1px solid rgba(247,244,239,0.15)", color: "#F7F4EF" }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#F7F4EF" }} />
                <Bar dataKey="allow" stackId="a" fill="#22c55e" />
                <Bar dataKey="flag" stackId="a" fill="#FF6B35" />
                <Bar dataKey="redact" stackId="a" fill="#f59e0b" />
                <Bar dataKey="deny" stackId="a" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-white/5 border border-white/10 p-5">
          <div className="eyebrow text-[#FFD98A]" style={{ fontSize: 10 }}>PERFORMANCE</div>
          <div className="mt-4 space-y-4">
            {perfCards.map((c) => (
              <div key={c.label} data-testid={c.testid}>
                <div className="text-xs text-cream/60">{c.label}</div>
                <div className="font-display text-3xl font-bold">
                  {c.value}<span className="text-lg text-cream/50">{c.unit}</span>
                </div>
              </div>
            ))}
            <Link to="/app/decisions" className="btn-ghost-dark text-xs w-full justify-between">
              View decisions <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </div>

      {/* Live feed row */}
      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2">
          <LiveFeed status={status} decisions={liveDecisions} lastAt={lastAt} subscribers={subscribers} />
        </div>

        <div className="rounded-xl bg-white/5 border border-white/10 p-5 flex flex-col">
          <div className="eyebrow text-[#FFD98A]" style={{ fontSize: 10 }}>SESSION</div>
          <div className="mt-3 space-y-3 text-sm">
            <StatRow label="Live decisions this session" value={liveDelta.total} tid="session-live-count" />
            <StatRow label="Deny" value={liveDelta.deny} color="#ef4444" tid="session-live-deny" />
            <StatRow label="Redact" value={liveDelta.redact} color="#f59e0b" tid="session-live-redact" />
            <StatRow label="Flag" value={liveDelta.flag} color="#FF6B35" tid="session-live-flag" />
            <StatRow label="Allow" value={liveDelta.allow} color="#22c55e" tid="session-live-allow" />
          </div>
          <div className="mt-auto pt-4 text-[11px] text-cream/50 border-t border-white/10 mt-4">
            Session counters reset on refresh. Server totals persist.
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        {[
          { title: "Policy Health", body: "6 active · 0 stale · latest bundle 2 min ago", icon: ShieldCheck, tid: "widget-policy-health" },
          { title: "Recent Alerts", body: `${(stats?.counts?.deny ?? 0) + liveDelta.deny} deny · ${(stats?.counts?.flag ?? 0) + liveDelta.flag} flag events`, icon: AlertTriangle, tid: "widget-recent-alerts" },
          { title: "Estimated Savings", body: `$${(((stats?.counts?.deny ?? 0) + liveDelta.deny) * 0.4).toFixed(2)} on blocked provider calls`, icon: DollarSign, tid: "widget-savings" },
        ].map((w) => (
          <div key={w.title} data-testid={w.tid} className="rounded-xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-[#FFD98A]">
              <w.icon size={14} />
              <div className="eyebrow" style={{ fontSize: 10 }}>{w.title}</div>
            </div>
            <div className="mt-2 text-cream/85 text-sm">{w.body}</div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function StatRow({ label, value, color, tid }) {
  return (
    <div data-testid={tid} className="flex items-center justify-between">
      <span className="text-cream/70">{label}</span>
      <span className="font-mono font-bold" style={color ? { color } : {}}>{value}</span>
    </div>
  );
}

// Module-scoped memo of ids we've already added to the chart bucket
// (so re-renders don't double-count).
const chartDeltaSeen = new Set();
